/**
 * EmailRecipientsAdmin — admin UI for the KB-doc-email recipient registry.
 *
 * Phase A step 3 of DR-2026-171 / SPEC-EMAIL-001.
 *
 * Route: /qms/recipients
 * Backend: /@qms/email-recipients (GET / POST / PATCH / DELETE-as-405)
 *
 * Patterns follow ContributorAdmin.jsx (table + modal + filter bar) and
 * DocumentRegister.jsx (search input + pagination + Helmet title).
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Header,
  Segment,
  Table,
  Input,
  Menu,
  Label,
  Loader,
  Button,
  Pagination,
  Icon,
  Modal,
  Form,
  Message,
  Confirm,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi } from '../../api';

const PAGE_SIZE = 50;
const MAX_DISPLAY_NAME = 200;
const MAX_NOTES = 500;

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const EmailRecipientsAdmin = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('true'); // 'true' | 'false' | 'all'
  const [page, setPage] = useState(1);

  // Add modal
  const [showAdd, setShowAdd] = useState(false);
  const [addEmail, setAddEmail] = useState('');
  const [addDisplayName, setAddDisplayName] = useState('');
  const [addNotes, setAddNotes] = useState('');
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addError, setAddError] = useState(null);

  // Edit modal
  const [editRow, setEditRow] = useState(null); // null or the row object
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState(null);

  // Soft-delete / Reactivate confirm
  const [confirmAction, setConfirmAction] = useState(null);
  // confirmAction shape: { row, nextActive: boolean } | null

  const fetchData = () => {
    setLoading(true);
    setError(null);
    const qs = new URLSearchParams();
    qs.set('active', activeFilter);
    if (search) qs.set('search', search);
    qs.set('page', page);
    qs.set('page_size', PAGE_SIZE);

    fetchApi(`${getApiPath()}/@qms/email-recipients?${qs.toString()}`)
      .then((json) => {
        setItems(json.items || []);
        setTotal(json.items_total || 0);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || String(err));
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter, search, page]);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const openAdd = () => {
    setAddEmail('');
    setAddDisplayName('');
    setAddNotes('');
    setAddError(null);
    setShowAdd(true);
  };

  const submitAdd = () => {
    setAddError(null);
    const email = addEmail.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) {
      setAddError('Email format invalid');
      return;
    }
    if (addDisplayName.length > MAX_DISPLAY_NAME) {
      setAddError(`Display name max ${MAX_DISPLAY_NAME} chars`);
      return;
    }
    if (addNotes.length > MAX_NOTES) {
      setAddError(`Notes max ${MAX_NOTES} chars`);
      return;
    }
    setAddSubmitting(true);
    fetchApi(`${getApiPath()}/@qms/email-recipients`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        display_name: addDisplayName || null,
        notes: addNotes || null,
      }),
    })
      .then(() => {
        setShowAdd(false);
        setAddSubmitting(false);
        fetchData();
      })
      .catch((err) => {
        setAddSubmitting(false);
        // 409 from backend includes existing_id in body
        let msg = err.message || String(err);
        if (err.response && err.response.body && err.response.body.error) {
          msg = err.response.body.error;
          if (err.response.body.existing_id) {
            msg += ` (existing id: ${err.response.body.existing_id})`;
          }
        }
        setAddError(msg);
      });
  };

  const openEdit = (row) => {
    setEditRow(row);
    setEditDisplayName(row.display_name || '');
    setEditNotes(row.notes || '');
    setEditError(null);
  };

  const submitEdit = () => {
    if (!editRow) return;
    setEditError(null);
    if (editDisplayName.length > MAX_DISPLAY_NAME) {
      setEditError(`Display name max ${MAX_DISPLAY_NAME} chars`);
      return;
    }
    if (editNotes.length > MAX_NOTES) {
      setEditError(`Notes max ${MAX_NOTES} chars`);
      return;
    }
    setEditSubmitting(true);
    fetchApi(`${getApiPath()}/@qms/email-recipients/${editRow.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        display_name: editDisplayName || null,
        notes: editNotes || null,
      }),
    })
      .then(() => {
        setEditRow(null);
        setEditSubmitting(false);
        fetchData();
      })
      .catch((err) => {
        setEditSubmitting(false);
        setEditError(err.message || String(err));
      });
  };

  const submitToggleActive = () => {
    if (!confirmAction) return;
    const { row, nextActive } = confirmAction;
    fetchApi(`${getApiPath()}/@qms/email-recipients/${row.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ active: nextActive }),
    })
      .then(() => {
        setConfirmAction(null);
        fetchData();
      })
      .catch((err) => {
        let msg = err.message || String(err);
        if (err.response && err.response.body && err.response.body.error) {
          msg = err.response.body.error;
        }
        setError(msg);
        setConfirmAction(null);
      });
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <Container id="polyglot-qms-recipients" style={{ paddingTop: '2em', paddingBottom: '2em' }}>
      <Helmet title="Email Recipients — LJUSA TIDER" />
      <Link to="/qms/dashboard">&larr; Back to QMS Dashboard</Link>

      <Header as="h1" style={{ marginTop: '0.5em' }}>
        Email Recipients
      </Header>
      <p style={{ color: '#666' }}>
        Shared platform-wide recipient registry. Used when emailing KB documents to one or more recipients.
        Per SPEC-EMAIL-001 / DR-2026-171.
      </p>

      <Segment>
        <Menu secondary stackable>
          <Menu.Item header>Show:</Menu.Item>
          <Menu.Item
            name="active"
            active={activeFilter === 'true'}
            onClick={() => { setActiveFilter('true'); setPage(1); }}
          >
            Active
          </Menu.Item>
          <Menu.Item
            name="inactive"
            active={activeFilter === 'false'}
            onClick={() => { setActiveFilter('false'); setPage(1); }}
          >
            Inactive
          </Menu.Item>
          <Menu.Item
            name="all"
            active={activeFilter === 'all'}
            onClick={() => { setActiveFilter('all'); setPage(1); }}
          >
            All
          </Menu.Item>
          <Menu.Menu position="right">
            <Menu.Item>
              <Input
                placeholder="Search email, name, or notes..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={(e) => { if (e.key === 'Enter') handleSearch(); }}
                action={{ icon: 'search', onClick: handleSearch }}
              />
            </Menu.Item>
            <Menu.Item>
              <Button primary onClick={openAdd}>
                <Icon name="add" /> Add recipient
              </Button>
            </Menu.Item>
          </Menu.Menu>
        </Menu>
      </Segment>

      {error && (
        <Message negative onDismiss={() => setError(null)}>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {loading ? (
        <Loader active inline="centered" content="Loading recipients..." />
      ) : items.length === 0 ? (
        <Segment placeholder>
          <Header icon>
            <Icon name="mail" />
            No recipients found
          </Header>
          <p>Add one to start sending KB docs by email.</p>
        </Segment>
      ) : (
        <>
          <Table celled striped>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Email</Table.HeaderCell>
                <Table.HeaderCell>Display Name</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Notes</Table.HeaderCell>
                <Table.HeaderCell>Registered</Table.HeaderCell>
                <Table.HeaderCell textAlign="right">Actions</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {items.map((row) => (
                <Table.Row
                  key={row.id}
                  style={!row.active ? { opacity: 0.55 } : undefined}
                >
                  <Table.Cell>{row.email}</Table.Cell>
                  <Table.Cell>{row.display_name || <em style={{ color: '#999' }}>—</em>}</Table.Cell>
                  <Table.Cell>
                    {row.active ? (
                      <Label color="green" size="small">active</Label>
                    ) : (
                      <Label color="grey" size="small">inactive</Label>
                    )}
                  </Table.Cell>
                  <Table.Cell style={{ fontSize: '0.9em', color: '#666' }}>
                    {row.notes || <em style={{ color: '#999' }}>—</em>}
                  </Table.Cell>
                  <Table.Cell style={{ fontSize: '0.9em', color: '#666' }}>
                    {row.opt_in_at ? new Date(row.opt_in_at).toISOString().slice(0, 10) : ''}
                    {row.created_by && (
                      <div style={{ fontSize: '0.85em' }}>by {row.created_by}</div>
                    )}
                  </Table.Cell>
                  <Table.Cell textAlign="right">
                    <Button size="tiny" basic onClick={() => openEdit(row)}>
                      <Icon name="edit" /> Edit
                    </Button>
                    {row.active ? (
                      <Button
                        size="tiny"
                        basic
                        color="grey"
                        onClick={() => setConfirmAction({ row, nextActive: false })}
                      >
                        <Icon name="ban" /> Deactivate
                      </Button>
                    ) : (
                      <Button
                        size="tiny"
                        basic
                        color="green"
                        onClick={() => setConfirmAction({ row, nextActive: true })}
                      >
                        <Icon name="redo" /> Reactivate
                      </Button>
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>

          <Segment basic textAlign="center">
            <span style={{ color: '#666', marginRight: '1em' }}>
              {total} recipient{total === 1 ? '' : 's'}
            </span>
            {totalPages > 1 && (
              <Pagination
                activePage={page}
                totalPages={totalPages}
                onPageChange={(_e, { activePage }) => setPage(activePage)}
              />
            )}
          </Segment>
        </>
      )}

      {/* ====================== Add Modal ====================== */}
      <Modal open={showAdd} size="small" onClose={() => setShowAdd(false)} closeIcon>
        <Modal.Header>Add Recipient</Modal.Header>
        <Modal.Content>
          {addError && (
            <Message negative>
              <p>{addError}</p>
            </Message>
          )}
          <Form>
            <Form.Field required>
              <label>Email</label>
              <Input
                value={addEmail}
                onChange={(e) => setAddEmail(e.target.value)}
                placeholder="recipient@example.com"
                autoFocus
              />
            </Form.Field>
            <Form.Field>
              <label>Display name <span style={{ color: '#999', fontWeight: 'normal' }}>(optional)</span></label>
              <Input
                value={addDisplayName}
                onChange={(e) => setAddDisplayName(e.target.value)}
                placeholder="Full name as shown to senders"
              />
            </Form.Field>
            <Form.Field>
              <label>Notes <span style={{ color: '#999', fontWeight: 'normal' }}>(optional — organisation, role, why on the list)</span></label>
              <Form.TextArea
                value={addNotes}
                onChange={(e) => setAddNotes(e.target.value)}
                rows={3}
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowAdd(false)} disabled={addSubmitting}>Cancel</Button>
          <Button primary onClick={submitAdd} loading={addSubmitting} disabled={addSubmitting}>
            <Icon name="add" /> Add
          </Button>
        </Modal.Actions>
      </Modal>

      {/* ====================== Edit Modal ====================== */}
      <Modal open={!!editRow} size="small" onClose={() => setEditRow(null)} closeIcon>
        <Modal.Header>Edit Recipient</Modal.Header>
        <Modal.Content>
          {editError && (
            <Message negative>
              <p>{editError}</p>
            </Message>
          )}
          <Form>
            <Form.Field>
              <label>Email <span style={{ color: '#999', fontWeight: 'normal' }}>(read-only — soft-delete this row and create a new one if the email changes)</span></label>
              <Input value={editRow?.email || ''} disabled />
            </Form.Field>
            <Form.Field>
              <label>Display name</label>
              <Input
                value={editDisplayName}
                onChange={(e) => setEditDisplayName(e.target.value)}
              />
            </Form.Field>
            <Form.Field>
              <label>Notes</label>
              <Form.TextArea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                rows={3}
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setEditRow(null)} disabled={editSubmitting}>Cancel</Button>
          <Button primary onClick={submitEdit} loading={editSubmitting} disabled={editSubmitting}>
            <Icon name="save" /> Save
          </Button>
        </Modal.Actions>
      </Modal>

      {/* ====================== Confirm soft-delete / reactivate ====================== */}
      <Confirm
        open={!!confirmAction}
        header={
          confirmAction?.nextActive
            ? 'Reactivate recipient?'
            : 'Deactivate recipient?'
        }
        content={
          confirmAction?.nextActive
            ? `Recipient ${confirmAction.row.email} will reappear in the send picker.`
            : `Recipient ${confirmAction?.row.email} won't appear in the send picker anymore. Historical send records preserve their identity. You can reactivate later.`
        }
        onCancel={() => setConfirmAction(null)}
        onConfirm={submitToggleActive}
      />
    </Container>
  );
};

export default EmailRecipientsAdmin;
